/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useMemo } from 'react';
import { AppRoutes } from '@/constants/routes.ts';
import { Utils } from '@/utils/utils.ts';
import { NavLink } from 'react-router';
import { classNames } from 'primereact/utils';
import { MonitorProcessData } from '@/types/models';
import { useJobsProcessInfoData } from './JobsMonitorContext';
import { Skeleton } from 'primereact/skeleton';
import { useProjectPath } from '@/hooks/useProjectPath';

interface Props {
  className?: string;
  monitorData: MonitorProcessData;
}
export const ProjectInfo: React.FC<Props> = ({ className, monitorData }) => {
  const { processesInfoData } = useJobsProcessInfoData();

  const { toBrowserProjectPath } = useProjectPath();

  const rowProcessInfo = useMemo(() => {
    if (processesInfoData && monitorData) {
      const rowInfo = processesInfoData?.find(
        (el) => el.fecthStatus === 'sucessfull' && el.processId == monitorData.id
      );
      return rowInfo;
    }
  }, [processesInfoData, monitorData]);

  const isFetchingInfo = useMemo(() => {
    if (!rowProcessInfo) {
      const emptyRowData = processesInfoData?.some(
        (el) => el.processId == monitorData.id && el.fecthStatus === 'failed'
      );
      return !emptyRowData && !monitorData.id.startsWith('dyna~') && true;
    }
    return rowProcessInfo.fecthStatus === 'pending';
  }, [rowProcessInfo, monitorData, processesInfoData]);

  const url = useMemo(() => {
    if (!rowProcessInfo?.data) {
      return '#';
    }
    return (
      AppRoutes.projects.detail.url(toBrowserProjectPath(rowProcessInfo.data.link)) +
      Utils.buildWithQueryParamsUrl({ tabs: '0' })
    );
  }, [rowProcessInfo, toBrowserProjectPath]);

  return (
    <>
      {isFetchingInfo ? (
        <Skeleton width={'100%'} height="1rem" />
      ) : (
        <NavLink className={classNames(className, 'text-color font-medium hover:text-primary')} to={url}>
          <span className={className}>
            {rowProcessInfo?.data ? rowProcessInfo.data.project_name : monitorData.project_path}
          </span>
        </NavLink>
      )}
    </>
  );
};
